/**
 * Synchronisation mehrerer Browser-Tabs nach Gutachten-Speichern.
 * BroadcastChannel (bevorzugt) + localStorage-Fallback für andere Tabs.
 * Eigene Tab-ID verhindert doppelte Refetches im auslösenden Tab.
 */

const CHANNEL_NAME = 'valest-gutachten-tab-sync';
const LS_KEY = 'valest-gutachten-updated';

const TAB_ID =
  (typeof crypto !== 'undefined' && crypto.randomUUID?.()) ||
  `tab-${Date.now()}-${Math.random()}`;

/** Wiederverwendeter Kanal nur zum Senden (ohne onmessage → keine Doppel-Handler). */
let outboundChannel = null;

const getOutboundBroadcastChannel = () => {
  if (typeof BroadcastChannel === 'undefined') return null;
  if (!outboundChannel) {
    try {
      outboundChannel = new BroadcastChannel(CHANNEL_NAME);
    } catch (_) {
      return null;
    }
  }
  return outboundChannel;
};

/**
 * Nach erfolgreichem PUT — andere Tabs können neu laden.
 * @param {string|number} gutachtenId
 * @param {string} [sourceTabId] — für Unmount-Flush vom gleichen Tab explizit setzen (sonst TAB_ID)
 */
export const broadcastGutachtenUpdated = (gutachtenId, sourceTabId = TAB_ID) => {
  if (gutachtenId == null || gutachtenId === '') return;
  const id = String(gutachtenId);
  const payload = {
    type: 'gutachten-updated',
    gutachtenId: id,
    sourceTabId,
    t: Date.now(),
  };

  const bc = getOutboundBroadcastChannel();
  if (bc) {
    try {
      bc.postMessage(payload);
    } catch (_) {
      /* ignore */
    }
  }

  try {
    localStorage.setItem(LS_KEY, JSON.stringify({ id, sourceTabId, t: Date.now() }));
    localStorage.removeItem(LS_KEY);
  } catch (_) {
    /* ignore */
  }
};

/**
 * @param {(gutachtenId: string) => void} handler
 * @returns {() => void} unsubscribe
 */
export const subscribeGutachtenUpdated = (handler) => {
  if (typeof BroadcastChannel !== 'undefined') {
    try {
      const bc = new BroadcastChannel(CHANNEL_NAME);
      bc.onmessage = (ev) => {
        const d = ev?.data;
        if (d?.type !== 'gutachten-updated' || !d.gutachtenId) return;
        if (d.sourceTabId === TAB_ID) return;
        handler(String(d.gutachtenId));
      };
      return () => bc.close();
    } catch (_) {
      /* fallback below */
    }
  }

  const onStorage = (e) => {
    if (e.key !== LS_KEY || !e.newValue) return;
    try {
      const p = JSON.parse(e.newValue);
      if (p?.sourceTabId === TAB_ID) return;
      if (p?.id) handler(String(p.id));
    } catch (_) {
      /* ignore */
    }
  };
  window.addEventListener('storage', onStorage);
  return () => window.removeEventListener('storage', onStorage);
};
